import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X, Star } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { contactService } from '../../services/contact.service';
import type { Contact, ContactInput } from '../../types/crm';
import Loading from '../Loading';

interface ContactListProps {
  // Filter by operator when set. When undefined, show all contacts in the account.
  operatorId?: number;
}

const createContactSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  title: z.string().optional(),
  phone: z.string().optional(),
  mobile: z.string().optional(),
  is_primary: z.boolean().optional(),
});

type CreateContactFormData = z.infer<typeof createContactSchema>;

export const ContactList = ({ operatorId }: ContactListProps) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateContactFormData>({
    resolver: zodResolver(createContactSchema),
  });

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorId]);

  const loadContacts = async () => {
    setIsLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (operatorId !== undefined) {
        params['q[operator_id_eq]'] = operatorId;
      }
      const result = await contactService.getAll(params);
      setContacts(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const onCreate = async (data: CreateContactFormData) => {
    setIsCreating(true);
    try {
      const input: ContactInput = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email || null,
        title: data.title || null,
        phone: data.phone || null,
        mobile: data.mobile || null,
        is_primary: data.is_primary ?? false,
        operator_id: operatorId ?? null,
      };
      await contactService.create(input);
      toast.success('Contact created');
      setShowCreateModal(false);
      reset();
      loadContacts();
    } catch (error) {
      // The is_primary uniqueness error from the server bubbles up here as a toast.
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Contacts</h3>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary btn-sm">
          <Plus className="h-4 w-4 inline mr-1" />
          Add Contact
        </button>
      </div>

      {isLoading ? (
        <Loading />
      ) : contacts.length === 0 ? (
        <div className="text-center text-gray-500 py-8">No contacts found.</div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                {operatorId === undefined && (
                  <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Operator</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contacts.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    <span className="flex items-center gap-1">
                      {c.is_primary && <Star className="h-4 w-4 text-yellow-500 fill-current" aria-label="Primary contact" />}
                      {c.full_name}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{c.title || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{c.email || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{c.phone || c.mobile || '—'}</td>
                  {operatorId === undefined && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{c.operator?.name || '—'}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-(--color-bg-scrim)" onClick={() => setShowCreateModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onCreate)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add New Contact</h3>
                    <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-500">
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="first_name" className="label">First Name</label>
                        <input {...register('first_name')} id="first_name" type="text" className="input" />
                        {errors.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>}
                      </div>
                      <div>
                        <label htmlFor="last_name" className="label">Last Name</label>
                        <input {...register('last_name')} id="last_name" type="text" className="input" />
                        {errors.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="title" className="label">Title</label>
                      <input {...register('title')} id="title" type="text" className="input" />
                    </div>

                    <div>
                      <label htmlFor="email" className="label">Email</label>
                      <input {...register('email')} id="email" type="email" className="input" />
                      {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="label">Phone</label>
                        <input {...register('phone')} id="phone" type="text" className="input" />
                      </div>
                      <div>
                        <label htmlFor="mobile" className="label">Mobile</label>
                        <input {...register('mobile')} id="mobile" type="text" className="input" />
                      </div>
                    </div>

                    {operatorId !== undefined && (
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input {...register('is_primary')} type="checkbox" className="rounded" />
                        Primary contact for this operator
                      </label>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isCreating} className="btn btn-primary">
                    {isCreating ? 'Creating...' : 'Create Contact'}
                  </button>
                  <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactList;
